/**
 * Login branding — build-time config from REACT_APP_* env vars written by
 * scripts/apply-brand.ps1 from the active brand's brand.json:
 *   settings.companyName (falls back to appName) -> REACT_APP_COMPANY_NAME
 *   colors.primary                                -> REACT_APP_BRAND_PRIMARY_COLOR
 *   colors.secondary                              -> REACT_APP_BRAND_SECONDARY_COLOR
 *
 * Deliberately separate from src/config/brand.js (which already reads
 * REACT_APP_PRIMARY_COLOR / REACT_APP_SECONDARY_COLOR for other screens) —
 * this module exists only for login branding infrastructure and does not
 * change what brand.js exposes.
 *
 * Every export fails open to the current ESS values, so this file has no
 * visible effect when no brand-specific env var is set (local `npm start`,
 * or a server still built from before these env vars existed).
 */
export const BRAND_NAME = process.env.REACT_APP_COMPANY_NAME || "ESS HRMS";

/**
 * True only when apply-brand.ps1 actually wrote a REACT_APP_COMPANY_NAME
 * for this build (i.e. the app was built through the brand pipeline for a
 * specific brand). This is false for the current ESS production build,
 * which predates the brand pipeline and never sets this env var — so any
 * UI gated on this flag (e.g. an extra "company name" label added to a
 * screen that never had one) stays 100% invisible/unchanged for ESS today,
 * and only appears once a real brand build supplies a company name.
 */
export const HAS_CUSTOM_COMPANY_NAME = Boolean(process.env.REACT_APP_COMPANY_NAME);

/**
 * Short badge text (mirrors src/config/brand.js's `shortName`, sourced from
 * the same REACT_APP_BRAND_SHORT env var written by apply-brand.ps1). Kept
 * here too so components that only need companyBrand.js don't have to pull
 * in the separate brand.js module just for a 3-4 letter fallback badge.
 */
export const BRAND_SHORT_NAME = process.env.REACT_APP_BRAND_SHORT || "ESS";

export const BRAND_SLOGAN =
  process.env.REACT_APP_SLOGAN || "إدارة أذكى لفريقك";

export const BRAND_PRIMARY_COLOR =
  process.env.REACT_APP_BRAND_PRIMARY_COLOR || "#293681";

export const BRAND_SECONDARY_COLOR =
  process.env.REACT_APP_BRAND_SECONDARY_COLOR || "#4274D9";

/**
 * Public path to the optional brand logo (see brands/<brand>/logo.png ->
 * public/brand-logo.png, copied by scripts/apply-brand.ps1). This is just
 * a path string — it does not check whether the file actually exists.
 * Callers (e.g. LoginPage) must render it defensively (e.g. an <img>
 * onError handler) since a brand may not ship a logo.png at all.
 */
export const BRAND_LOGO_PATH = `${process.env.PUBLIC_URL || ""}/brand-logo.png`;

const companyBrand = {
  BRAND_NAME,
  HAS_CUSTOM_COMPANY_NAME,
  BRAND_SHORT_NAME,
  BRAND_SLOGAN,
  BRAND_PRIMARY_COLOR,
  BRAND_SECONDARY_COLOR,
  BRAND_LOGO_PATH,
};

export default companyBrand;
